import React, { useState, useMemo } from 'react';
import { Select, Button, Tag, Typography, Tooltip, Popconfirm, Empty, Space } from 'antd';
import { 
    HolderOutlined, 
    ArrowUpOutlined, 
    ArrowDownOutlined, 
    DeleteOutlined, 
    EnvironmentOutlined, 
    ClearOutlined,
    PlusOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';

const { Text } = Typography;

export interface MunicipiosOrdenavelListProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    cidadesDisponiveis: { label: string; value: string }[];
    loading?: boolean;
    isDark?: boolean;
    minLimit?: number;
    maxLimit?: number;
}

const normalizar = (txt: any): string => {
    return String(txt || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
};

export const MunicipiosOrdenavelList: React.FC<MunicipiosOrdenavelListProps> = ({
    value = [],
    onChange,
    cidadesDisponiveis = [],
    loading = false,
    isDark = false,
    minLimit = 20,
    maxLimit = 25
}) => {
    const [searchVal, setSearchVal] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const selecionados = useMemo(() => value || [], [value]);
    const atingiuLimite = selecionados.length >= maxLimit;
    const atingiuMinimo = selecionados.length >= minLimit;
    const faltamParaMinimo = Math.max(0, minLimit - selecionados.length);

    const cidadesParaAdicionar = useMemo(() => {
        const setSelecionadas = new Set(selecionados.map(s => normalizar(s)));
        return cidadesDisponiveis.filter(c => !setSelecionadas.has(normalizar(c.value)));
    }, [cidadesDisponiveis, selecionados]);

    const handleSelectCidade = (cidade: string) => {
        if (!cidade) return;
        if (atingiuLimite) return;
        if (selecionados.includes(cidade)) {
            setSearchVal(null);
            setSearchValue('');
            return;
        }

        const novaLista = [...selecionados, cidade];
        onChange?.(novaLista);
        setSearchVal(null);
        setSearchValue('');
    };

    const handleRemoverCidade = (index: number) => {
        const novaLista = selecionados.filter((_, i) => i !== index);
        onChange?.(novaLista);
    };

    const handleSubir = (index: number) => {
        if (index <= 0) return;
        const novaLista = [...selecionados];
        const temp = novaLista[index - 1];
        novaLista[index - 1] = novaLista[index];
        novaLista[index] = temp;
        onChange?.(novaLista);
    };

    const handleDescer = (index: number) => {
        if (index >= selecionados.length - 1) return;
        const novaLista = [...selecionados];
        const temp = novaLista[index + 1];
        novaLista[index + 1] = novaLista[index];
        novaLista[index] = temp;
        onChange?.(novaLista);
    };

    const handleLimparTudo = () => {
        onChange?.([]);
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const novaLista = [...selecionados];
        const itemMovido = novaLista[draggedIndex];
        novaLista.splice(draggedIndex, 1);
        novaLista.splice(targetIndex, 0, itemMovido);

        onChange?.(novaLista);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div style={{ width: '100%' }}>
            {/* Estilo para barra de rolagem elegante */}
            <style>{`
                .municipios-lista-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .municipios-lista-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 8px;
                }
                .municipios-lista-scrollbar::-webkit-scrollbar-thumb {
                    background: ${isDark ? '#334155' : '#cbd5e1'};
                    border-radius: 8px;
                    transition: background 0.2s ease;
                }
                .municipios-lista-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: ${isDark ? '#475569' : '#94a3b8'};
                }
            `}</style>

            {/* Barra de Pesquisa e Inclusão */}
            <div style={{ marginBottom: 12 }}>
                <Select
                    showSearch
                    allowClear
                    loading={loading}
                    value={searchVal}
                    searchValue={searchValue}
                    onSearch={(val) => setSearchValue(val)}
                    onSelect={(val: string) => {
                        if (val) {
                            handleSelectCidade(val);
                        }
                    }}
                    onChange={(val) => {
                        if (!val) {
                            setSearchVal(null);
                            setSearchValue('');
                        }
                    }}
                    onBlur={() => {
                        setSearchValue('');
                    }}
                    placeholder={
                        atingiuLimite
                            ? `Limite máximo de ${maxLimit} municípios atingido`
                            : selecionados.length < minLimit
                            ? `Pesquise e selecione município (${selecionados.length}/${minLimit} mín.)...`
                            : `Pesquise e selecione mais municípios (até ${maxLimit})...`
                    }
                    disabled={atingiuLimite}
                    options={cidadesParaAdicionar}
                    filterOption={(input, option) =>
                        normalizar(option?.label).includes(normalizar(input))
                    }
                    style={{ width: '100%', fontSize: '13px' }}
                />
            </div>

            {/* Cabeçalho da Lista: Contador e Ações */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                    padding: '0 2px'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Tag 
                        color={
                            atingiuLimite 
                                ? 'purple' 
                                : atingiuMinimo 
                                ? 'success' 
                                : selecionados.length > 0 
                                ? 'warning' 
                                : 'default'
                        }
                        style={{ margin: 0, fontWeight: 600, fontSize: '11.5px', borderRadius: 12, padding: '1px 9px' }}
                    >
                        <CheckCircleOutlined /> {selecionados.length} / {maxLimit} selecionados
                    </Tag>

                    {selecionados.length < minLimit ? (
                        <Text style={{ fontSize: '11.5px', color: '#f59e0b', fontWeight: 500 }}>
                            {selecionados.length === 0 
                                ? `(Mínimo de ${minLimit} e máximo de ${maxLimit})` 
                                : `(Faltam ${faltamParaMinimo} para o mínimo de ${minLimit})`
                            }
                        </Text>
                    ) : (
                        <Text style={{ fontSize: '11.5px', color: '#10b981', fontWeight: 500 }}>
                            ✓ Quantidade válida ({minLimit} a {maxLimit} municípios)
                        </Text>
                    )}

                    {selecionados.length > 0 && (
                        <Text type="secondary" style={{ fontSize: '11.5px', color: isDark ? '#94a3b8' : '#64748b' }}>
                            • Arraste ou use as setas para ordenar
                        </Text>
                    )}
                </div>

                {selecionados.length > 0 && (
                    <Popconfirm
                        title="Deseja remover todos os municípios da lista?"
                        okText="Sim, limpar"
                        cancelText="Cancelar"
                        onConfirm={handleLimparTudo}
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<ClearOutlined />}
                            style={{ padding: 0, fontSize: '12px' }}
                        >
                            Limpar lista
                        </Button>
                    </Popconfirm>
                )}
            </div>

            {/* Container da Listagem Vertical com Drag and Drop */}
            <div
                className="municipios-lista-scrollbar"
                style={{
                    border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                    borderRadius: 8,
                    backgroundColor: isDark ? '#141414' : '#f8fafc',
                    maxHeight: 380,
                    overflowY: 'auto',
                    padding: '8px',
                    paddingRight: '6px',
                    transition: 'all 0.2s ease',
                    scrollbarWidth: 'thin',
                    scrollbarColor: isDark ? '#334155 transparent' : '#cbd5e1 transparent'
                }}
            >
                {selecionados.length === 0 ? (
                    <div
                        style={{
                            padding: '36px 16px',
                            textAlign: 'center',
                            color: isDark ? '#94a3b8' : '#64748b'
                        }}
                    >
                        <EnvironmentOutlined style={{ fontSize: 32, color: '#0077b6', marginBottom: 10, display: 'block' }} />
                        <Text strong style={{ fontSize: '13px', color: isDark ? '#ffffffd9' : '#334155', display: 'block' }}>
                            Nenhum município selecionado
                        </Text>
                        <span style={{ fontSize: '12px' }}>
                            Pesquise no campo acima para adicionar entre {minLimit} e {maxLimit} municípios em destaque no Slide 2.
                        </span>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {selecionados.map((cidade, index) => {
                            const isBeingDragged = draggedIndex === index;
                            const isOver = dragOverIndex === index && draggedIndex !== index;

                            return (
                                <div
                                    key={`${cidade}-${index}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onDrop={(e) => handleDrop(e, index)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '7px 10px',
                                        borderRadius: 6,
                                        backgroundColor: isBeingDragged
                                            ? (isDark ? '#1e293b' : '#e2e8f0')
                                            : (isDark ? '#1f1f1f' : '#ffffff'),
                                        border: isOver
                                            ? '2px solid #1890ff'
                                            : `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                                        boxShadow: isDark 
                                            ? '0 1px 2px rgba(0,0,0,0.3)' 
                                            : '0 1px 3px rgba(0,0,0,0.04)',
                                        opacity: isBeingDragged ? 0.45 : 1,
                                        cursor: 'grab',
                                        transition: 'background-color 0.15s, border-color 0.15s, opacity 0.15s',
                                        userSelect: 'none'
                                    }}
                                >
                                    {/* Lado Esquerdo: Grip Handle + Número de Ordem + Nome */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                                        <Tooltip title="Arraste para reposicionar">
                                            <HolderOutlined 
                                                style={{ 
                                                    color: isDark ? '#64748b' : '#94a3b8', 
                                                    fontSize: 15,
                                                    cursor: 'grab',
                                                    flexShrink: 0
                                                }} 
                                            />
                                        </Tooltip>

                                        {/* Badge da posição no Slide 2 */}
                                        <div
                                            style={{
                                                minWidth: 26,
                                                height: 22,
                                                padding: '0 4px',
                                                borderRadius: 4,
                                                backgroundColor: isDark ? '#1e293b' : '#f0f9ff',
                                                border: `1px solid ${isDark ? '#334155' : '#bae6fd'}`,
                                                color: '#3c4244',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 700,
                                                fontSize: '11px',
                                                flexShrink: 0
                                            }}
                                        >
                                            {index + 1}
                                        </div>

                                        {/* Nome da Cidade */}
                                        <Text
                                            style={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: isDark ? '#ffffffd9' : '#1e293b',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                            title={cidade}
                                        >
                                            {cidade}
                                        </Text>
                                    </div>

                                    {/* Lado Direito: Ações (Subir, Descer, Remover) */}
                                    <Space size={2} style={{ flexShrink: 0 }}>
                                        {/* <Tooltip title="Mover para cima">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ArrowUpOutlined style={{ fontSize: 11 }} />}
                                                disabled={index === 0}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSubir(index);
                                                }}
                                                style={{ width: 24, height: 24, padding: 0 }}
                                            />
                                        </Tooltip>

                                        <Tooltip title="Mover para baixo">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ArrowDownOutlined style={{ fontSize: 11 }} />}
                                                disabled={index === selecionados.length - 1}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDescer(index);
                                                }}
                                                style={{ width: 24, height: 24, padding: 0 }}
                                            />
                                        </Tooltip> */}

                                        <Tooltip title="Remover da lista">
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoverCidade(index);
                                                }}
                                                style={{ width: 24, height: 24, padding: 0 }}
                                            />
                                        </Tooltip>
                                    </Space>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
